// Reactive recompute driver: debounces, dispatches compute requests to the
// Web Worker, and exposes results via the `computeStatus` rune store.
//
// Section 5 fills the worker's transform registry. Until then, requests for
// pipelines that include unimplemented step types will surface an error in
// `computeStatus.error` (the UI shows it as a non-blocking warning).

import type { Pipeline } from '$lib/model/types';
import { projectStore } from '$lib/state/project.svelte';
import { hashSourceBytes } from './hash';
import { dispatchCompute, type DispatchHandle } from './worker-client';
import { sha256Hex } from '$lib/util/sha256';
import { canonicalJson } from '$lib/util/canonicalJson';

export interface PerStepOutputs {
  stepId: string;
  images: ImageData[];
}

export interface ComputeStatus {
  running: boolean;
  error: string | null;
  outputs: PerStepOutputs[];
  lastSourceHash: string | null;
}

export const computeStatus = $state<ComputeStatus>({
  running: false,
  error: null,
  outputs: [],
  lastSourceHash: null,
});

let currentDispatch: DispatchHandle | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let cachedSourceHash: string | null = null;
let cachedSourceBlob: Blob | null = null;

let activeSourceBitmap: ImageBitmap | null = null;
let activeSourceBlob: Blob | null = null;

export function setActiveSource(
  bitmap: ImageBitmap | null,
  blob: Blob | null,
): void {
  if (activeSourceBitmap && activeSourceBitmap !== bitmap) {
    activeSourceBitmap.close();
  }
  activeSourceBitmap = bitmap;
  activeSourceBlob = blob;
  cachedSourceHash = null;
  cachedSourceBlob = null;
  triggerRecompute();
}

export function getActiveSourceBitmap(): ImageBitmap | null {
  return activeSourceBitmap;
}

export function getActiveSourceBlob(): Blob | null {
  return activeSourceBlob;
}

export function triggerRecompute(): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void runOnce(projectStore.pipeline);
  }, 100);
}

async function runOnce(pipeline: Pipeline): Promise<void> {
  if (!activeSourceBlob) {
    computeStatus.outputs = [];
    return;
  }
  if (currentDispatch) currentDispatch.cancel();

  computeStatus.running = true;
  computeStatus.error = null;

  try {
    if (cachedSourceHash === null || cachedSourceBlob !== activeSourceBlob) {
      cachedSourceHash = await hashSourceBytes(activeSourceBlob);
      cachedSourceBlob = activeSourceBlob;
    }
    computeStatus.lastSourceHash = cachedSourceHash;

    // Snapshot Svelte 5 state to plain objects before posting across the
    // worker boundary. Also mix the settings that affect compute (target
    // dimensions + DPI) into the root parent hash so cache invalidates when
    // they change.
    const settingsSnap = $state.snapshot(projectStore.settings);
    const pipelineSnap = $state.snapshot(pipeline) as Pipeline;
    const computeKey = canonicalJson({
      target_width: settingsSnap.target_width,
      target_height: settingsSnap.target_height,
      units: settingsSnap.units,
      dpi: settingsSnap.dpi,
    });
    const rootParentHash = await sha256Hex(
      cachedSourceHash + computeKey,
    );

    const handle = dispatchCompute(
      pipelineSnap,
      settingsSnap,
      activeSourceBlob,
      rootParentHash,
    );
    currentDispatch = handle;
    const response = await handle.promise;
    if (currentDispatch === handle) {
      computeStatus.outputs = response.outputs;
    }
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return;
    const msg = err instanceof Error ? err.message : String(err);
    computeStatus.error = msg;
    computeStatus.outputs = [];
  } finally {
    if (currentDispatch?.promise) {
      // Only clear `running` if this is still the most recent dispatch.
    }
    computeStatus.running = false;
  }
}
